import { useEffect, useId, useRef, useState } from 'react';
import type { PersonDTO } from '@almanac/shared';
import { useCurrentUser } from '../lib/me.js';
import { useCreatePerson, usePeople } from '../lib/people.js';
import { PersonAvatar } from './PersonAvatar.js';

type SingleProps = {
  multi?: false;
  value: string | null;
  onChange: (id: string | null, displayName: string | null) => void;
};

type MultiProps = {
  multi: true;
  values: string[];
  onValuesChange: (ids: string[]) => void;
};

type CommonProps = {
  label?: string;
  placeholder?: string;
  autoFocus?: boolean;
  /**
   * Person IDs to omit from the dropdown. The "Of" field in the
   * person editor uses this to hide the current person from their
   * own dropdown — you can't be related to yourself.
   */
  excludeIds?: string[];
};

type Props = CommonProps & (SingleProps | MultiProps);

function newId(): string {
  return crypto.randomUUID();
}

/**
 * Combobox-style person picker. Two shapes:
 *  - Single (default): `value` + `onChange`. Used for "whose birthday"
 *    in the obligation drawer and for the inline-edit "Of" picker.
 *  - Multi (`multi: true`): `values` + `onValuesChange`. Used for
 *    anniversaries (two subjects) and the person editor's multi-link
 *    "Of" field.
 *
 * Inline-create stays available in both modes — typing a name and
 * pressing Enter creates the person and selects them in one motion.
 */
export function PersonPicker(props: Props) {
  const people = usePeople();
  const create = useCreatePerson();
  const me = useCurrentUser();

  const isMulti = props.multi === true;
  const selectedIds = isMulti ? props.values : props.value ? [props.value] : [];

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  // Display info for selected people — used to render the chips
  // (multi) or the input text (single).
  const selectedPeople = selectedIds
    .map((id) => people.data?.find((p) => p.id === id))
    .filter((p): p is PersonDTO => p !== undefined);
  const singleSelected: PersonDTO | null = !isMulti && selectedPeople[0] ? selectedPeople[0] : null;

  useEffect(() => {
    if (props.autoFocus) inputRef.current?.focus();
  }, [props.autoFocus]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      const target = e.target as Node | null;
      if (target && wrapperRef.current && !wrapperRef.current.contains(target)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const trimmed = query.trim();
  const excludeSet = new Set(props.excludeIds ?? []);
  // In multi mode, already-selected people stay in the dropdown but
  // render with a tick so re-clicking removes them. In single mode,
  // they're filtered out of the dropdown to avoid the "select the
  // same person again" no-op.
  // First-preference ordering: Me ("This is me", Settings → Account),
  // then household members, then everyone else — most documents are
  // about the household, so its people lead every picker. Stable sort
  // keeps each band alphabetical.
  const meId = me.data?.personId ?? null;
  const rank = (p: PersonDTO) => (p.id === meId ? 0 : p.household ? 1 : 2);
  const filtered = (people.data ?? [])
    .filter((p) => !excludeSet.has(p.id))
    .filter((p) =>
      trimmed === '' ? true : p.displayName.toLowerCase().includes(trimmed.toLowerCase()),
    )
    .sort((a, b) => rank(a) - rank(b));
  const exactMatch = filtered.some(
    (p) => p.displayName.toLowerCase() === trimmed.toLowerCase(),
  );
  const showCreate = trimmed.length > 0 && !exactMatch;

  function setSingle(id: string | null, name: string | null) {
    if (props.multi) return;
    props.onChange(id, name);
  }
  function setMulti(ids: string[]) {
    if (!props.multi) return;
    props.onValuesChange(ids);
  }

  function choose(person: PersonDTO) {
    if (isMulti) {
      const already = selectedIds.includes(person.id);
      setMulti(already ? selectedIds.filter((id) => id !== person.id) : [...selectedIds, person.id]);
      setQuery('');
      // Keep the popover open for multi-select so the user can stack picks.
      inputRef.current?.focus();
    } else {
      setSingle(person.id, person.displayName);
      setQuery('');
      setOpen(false);
    }
  }

  function createAndChoose() {
    if (!trimmed) return;
    const id = newId();
    create.mutate({ id, displayName: trimmed });
    if (isMulti) {
      setMulti([...selectedIds, id]);
      setQuery('');
      inputRef.current?.focus();
    } else {
      setSingle(id, trimmed);
      setQuery('');
      setOpen(false);
    }
  }

  function clearSingle() {
    setSingle(null, null);
    setQuery('');
    inputRef.current?.focus();
  }

  function removeFromMulti(id: string) {
    setMulti(selectedIds.filter((existing) => existing !== id));
  }

  // Single-mode input text: selection's name when collapsed, query
  // while typing. Multi-mode input text: always the live query — the
  // chips above the input show what's already selected.
  const displayValue = isMulti
    ? query
    : open
      ? query
      : singleSelected?.displayName ?? '';

  function handleFocus() {
    setOpen(true);
    if (!isMulti && singleSelected) setQuery('');
  }

  function handleKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[0] && !selectedIds.includes(filtered[0].id)) {
        choose(filtered[0]);
      } else if (showCreate) {
        createAndChoose();
      }
    } else if (e.key === 'Backspace' && isMulti && !query && selectedIds.length > 0) {
      // Convenience: backspace on an empty input removes the last chip.
      removeFromMulti(selectedIds[selectedIds.length - 1]!);
    }
  }

  return (
    <div ref={wrapperRef} className="person-picker">
      {props.label && <span className="field__label">{props.label}</span>}

      {isMulti && selectedPeople.length > 0 && (
        <div className="person-picker__chips" role="list">
          {selectedPeople.map((p) => (
            <span key={p.id} role="listitem" className="person-picker__chip">
              {p.avatarVersion && <PersonAvatar person={p} className="person-picker__chip-av" />}
              {p.displayName}
              <button
                type="button"
                onClick={() => removeFromMulti(p.id)}
                aria-label={`Remove ${p.displayName}`}
                className="person-picker__chip-remove"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="person-picker__field">
        <input
          ref={inputRef}
          className="field__input person-picker__input"
          type="text"
          name="person"
          autoComplete="off"
          placeholder={props.placeholder ?? "Person's name"}
          value={displayValue}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={handleFocus}
          onKeyDown={handleKey}
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
        />
        {!isMulti && singleSelected && !open && (
          <button
            type="button"
            className="person-picker__clear"
            onClick={clearSingle}
            aria-label={`Clear selected person ${singleSelected.displayName}`}
          >
            ×
          </button>
        )}
      </div>

      {open && (
        <div ref={popoverRef} className="person-picker__popover" id={listboxId} role="listbox">
          {filtered.length === 0 && !showCreate && (
            <div className="person-picker__empty">No people yet. Type a name to add one.</div>
          )}
          {filtered.map((p) => {
            const isSelected = selectedIds.includes(p.id);
            return (
              <button
                key={p.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={`person-picker__option ${isSelected ? 'person-picker__option--selected' : ''}`}
                onClick={() => choose(p)}
              >
                <PersonAvatar person={p} className="person-picker__option-av" />
                <span className="person-picker__option-mid">
                  <span className="person-picker__option-name">
                    {isMulti && isSelected ? '✓ ' : ''}
                    {p.id === meId ? `Me — ${p.displayName}` : p.displayName}
                  </span>
                  {p.id === meId ? (
                    <span className="person-picker__option-meta">you</span>
                  ) : (
                    p.relationship && (
                      <span className="person-picker__option-meta">{p.relationship}</span>
                    )
                  )}
                </span>
              </button>
            );
          })}
          {showCreate && (
            <button
              type="button"
              role="option"
              className="person-picker__option person-picker__option--create"
              onClick={createAndChoose}
            >
              + Add &ldquo;{trimmed}&rdquo;
            </button>
          )}
        </div>
      )}
    </div>
  );
}
